import { TransactionColumn } from "@common/types/views/transactionViews";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { MutableRefObject, useCallback, useRef } from "react";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";

type FocusableTransaction = Pick<TransactionListItem, "id">;

interface UseFocusedTransactionCellShortcutsParams {
    selectedCount: number;
    dataRef: MutableRefObject<FocusableTransaction[] | null>;
    openCellAction: (options: {
        columnId: string;
        rowId: string | number;
    }) => boolean;
    enabled?: boolean;
    isNoteShortcutDisabled?: boolean;
}

export const useFocusedTransactionCellShortcuts = ({
    selectedCount,
    dataRef,
    openCellAction,
    enabled = true,
    isNoteShortcutDisabled = false,
}: UseFocusedTransactionCellShortcutsParams) => {
    const focusedTransactionRef = useRef<FocusableTransaction | null>(null);

    const handleFocusedRowChange = useCallback(
        (_: number | null, row: FocusableTransaction | null) => {
            focusedTransactionRef.current = row;
        },
        [],
    );

    const openCellActionForFocusedRow = useCallback(
        (columnId: string): boolean => {
            if (selectedCount > 0) {
                return false;
            }

            const targetRowId =
                focusedTransactionRef.current?.id ?? dataRef.current?.[0]?.id;
            if (targetRowId !== undefined) {
                return openCellAction({ columnId, rowId: targetRowId });
            }

            return false;
        },
        [selectedCount, openCellAction, dataRef],
    );

    useKeyboardCommand({
        keys: "c",
        callback: () => openCellActionForFocusedRow(TransactionColumn.CATEGORY),
        options: {
            enabled,
            description: "Open category selector for focused row",
        },
    });

    useKeyboardCommand({
        keys: "m",
        callback: () => openCellActionForFocusedRow(TransactionColumn.NOTE),
        options: {
            enabled: enabled && !isNoteShortcutDisabled,
            description: "Open note editor for focused row",
            // An inline memo takes focus during keydown, so the key's default
            // would type an "m" into it.
            preventDefault: true,
        },
    });

    useKeyboardCommand({
        keys: "e",
        callback: () => openCellActionForFocusedRow(TransactionColumn.ENTITY),
        options: {
            enabled,
            description: "Open entity selector for focused row",
        },
    });

    useKeyboardCommand({
        keys: "g",
        callback: () => {
            const opened = openCellActionForFocusedRow(
                TransactionColumn.LEDGER,
            );
            if (!opened) {
                openCellActionForFocusedRow(TransactionColumn.ACCRUAL_LEDGER);
            }
        },
        options: {
            enabled,
            description: "Open cash ledger selector for focused row",
        },
    });

    return {
        handleFocusedRowChange,
    };
};
